/**
 * vin.js — разбор идентификационного номера транспортного средства.
 *
 * ── Что здесь принципиально ────────────────────────────────────────────────
 * VIN — это не база данных, а СТРОКА, в которой закодировано ограниченное
 * число фактов: страна и завод-изготовитель, год модели, контрольная цифра и
 * серийный номер. В нём НЕТ пробега, истории аварий, цвета, комплектации и
 * владельца. Любой сайт, обещающий «пробить историю по VIN бесплатно»,
 * достаёт эти сведения из СТОРОННИХ баз, а не из самого номера.
 *
 * Поэтому здесь честно разбирается ровно то, что закодировано, и отдельно
 * называется то, чего в номере нет.
 *
 * ── Контрольная цифра и главная ловушка ────────────────────────────────────
 * Девятый знак — контрольная цифра, вычисляемая по всем остальным (ISO 3779).
 * Она обязательна в Северной Америке (49 CFR 565) и Китае (GB 16735) и ловит
 * перебитый номер: изменить один знак и оставить сумму верной почти невозможно.
 *
 * ⚠️⚠️ Но многие изготовители девятый знак в контрольную цифру НЕ выделяют.
 * Проверять его у них значит объявлять поддельным исправный номер — ровно это
 * делают многие онлайн-декодеры. Кто выделяет, а кто нет, здесь не угадано, а
 * ИЗМЕРЕНО (22.10.2026) на 857 834 настоящих номерах легковых машин из
 * открытого реестра регистраций МВД Украины (CC BY 4.0): у Lada XTA девятый
 * знак сходится в 9 % номеров — ровно как у случайного знака (1 из 11), у
 * Hyundai Z94 — в 0 %, у Volkswagen XW8 — в 0,2 %; у BMW WBA — в 99,7 %, у
 * Skoda TMB — в 99,2 %. И наоборот: у части североамериканских и китайских
 * WMI, где цифра обязательна, экспортные машины её не несут (Honda 1HG —
 * 79 %, Honda LUC — 11 %). Поэтому решает измерение по WMI (данные —
 * public/data/vin/wmi.json), а правило региона — только там, где измерения нет.
 *
 * ── Год модели ─────────────────────────────────────────────────────────────
 * Десятый знак — год модели только там, где изготовитель его так заполняет.
 * Измерено там же: у Renault VF1 он совпал с годом регистрации в 13 % номеров,
 * у Ford Europe WF0 — в 7 %, у Mercedes-Benz WDB / WDD — в 22–30 %. Таким
 * номерам год не называется, а говорится, что его там нет.
 */

/* Буква → число для контрольной суммы (ISO 3779). I, O, Q в VIN не бывают. */
const TRANSLIT = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
    J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
    S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/* Год модели по 10-му знаку. Тридцатилетний цикл: одна и та же буква
   означает и 1988, и 2018 — снять неоднозначность помогает только
   североамериканское правило про 7-й знак (см. yearOf). */
const YEAR_CODES = 'ABCDEFGHJKLMNPRSTVWXY123456789';

/* Регионы, где девятый знак ОБЯЗАН быть контрольной цифрой, а десятый — годом:
   Северная Америка (1–5 и 7 — в таблице ISO 2021 весь ряд 7 отдан США;
   49 CFR 565) и Китай (L и H; GB 16735). Действует, только когда у WMI нет
   своего измерения — измерение сильнее правила (см. шапку). */
const MANDATORY = /^[1-57LH]/;
/* Лёгкие машины Северной Америки: 7-й знак снимает тридцатилетний цикл. */
const NA_LIGHT = /^[1-57]/;

/* Пороги по измерению: доля номеров WMI, где девятый знак сошёлся с расчётом /
   десятый — с годом регистрации (±1). Случайный знак сходится в 1 из 11
   (9 %), поэтому «не использует» — это меньше 30 %, а «использует» — 95 % и
   выше; между ними изготовитель ставит цифру лишь части машин (экспорт). */
export const CD_USED = 95;
export const CD_MIXED = 30;
export const YEAR_RELIABLE = 95;

/* Страна по первым двум знакам — таблица ISO 3780 «WMI World Codes»
   (ред. 13.04.2021), перенесённая сюда фактами, не файлом. Порядок второго
   знака в таблице: A–H, J–N, P, R–Z, 1–9, 0. Незанятые клетки (ряды D, F, G,
   0, свободные коды внутри рядов) не возвращают ничего: страну по ним
   назвать нельзя.

   ⚠️ До 22.10.2026 здесь стояли E → Марокко и H → Кот-д'Ивуар — по
   прежней редакции таблицы. В редакции 2021 года ряд E целиком отдан России
   (Tenet EDE, Evolute EAN, Sollers EBE), ряд H — Китаю (Xiaomi HXM, Li Auto
   HLX, VW Anhui HVW, Jetour HJR, NIO HJN), а Марокко и Кот-д'Ивуар переехали в
   CF–CG и AJ–AK. */
const COUNTRY_RANGES = [
    [/^A[ABCDEFGH]/, 'za'], [/^A[JK]/, 'ci'], [/^A[LM]/, 'ls'], [/^A[NP]/, 'bw'],
    [/^A[RS]/, 'na'], [/^A[TU]/, 'mg'], [/^A[VW]/, 'mu'], [/^A[XY]/, 'tn'],
    [/^A[Z1]/, 'cy'], [/^A[23]/, 'zw'], [/^A[45]/, 'mz'],
    [/^B[AB]/, 'ao'], [/^B[C]/, 'et'], [/^B[FG]/, 'ke'], [/^B[H]/, 'rw'],
    [/^B[L]/, 'ng'], [/^B[R]/, 'dz'], [/^B[T]/, 'sz'], [/^B[U]/, 'ug'], [/^B[34]/, 'ly'],
    [/^C[AB]/, 'eg'], [/^C[FG]/, 'ma'], [/^C[LM]/, 'zm'],
    [/^E/, 'ru'],
    [/^H/, 'cn'],
    [/^J/, 'jp'],
    [/^K[FGH]/, 'il'], [/^K[LMNPR]/, 'kr'], [/^K[ST]/, 'jo'], [/^K[123]/, 'kr'], [/^K[5]/, 'kg'],
    [/^L/, 'cn'],
    [/^M[ABCDE]/, 'in'], [/^M[FGHJK]/, 'id'], [/^M[LMNPR]/, 'th'], [/^M[S]/, 'mm'],
    [/^M[U]/, 'mn'], [/^M[X]/, 'kz'], [/^M[YZ1234567890]/, 'in'],
    [/^N[ABCDE]/, 'ir'], [/^N[FG]/, 'pk'], [/^N[J]/, 'iq'], [/^N[LMNPR]/, 'tr'],
    [/^N[ST]/, 'uz'], [/^N[V]/, 'az'], [/^N[X]/, 'tj'], [/^N[Y]/, 'am'],
    [/^N[12345]/, 'ir'], [/^N[78]/, 'tr'],
    [/^P[ABC]/, 'ph'], [/^P[FG]/, 'sg'], [/^P[LMNPR]/, 'my'], [/^P[ST]/, 'bd'], [/^P[567890]/, 'in'],
    [/^R[AB]/, 'ae'], [/^R[FGHJK]/, 'tw'], [/^R[LMN]/, 'vn'], [/^R[P]/, 'la'],
    [/^R[ST]/, 'sa'], [/^R[UVW]/, 'ru'], [/^R[1234567]/, 'hk'],
    [/^S[ABCDEFGHJKLM]/, 'gb'], [/^S[NPRST]/, 'de'], [/^S[UVWXYZ]/, 'pl'],
    [/^S[12]/, 'lv'], [/^S[3]/, 'ge'], [/^S[4]/, 'is'],
    [/^T[ABCDEFGH]/, 'ch'], [/^T[JKLMNP]/, 'cz'], [/^T[RSTUV]/, 'hu'], [/^T[WXYZ12]/, 'pt'],
    [/^T[345]/, 'rs'], [/^T[6]/, 'ad'], [/^T[78]/, 'nl'],
    [/^U[ABC]/, 'es'], [/^U[HJKLM]/, 'dk'], [/^U[NPR]/, 'ie'], [/^U[UVWX]/, 'ro'],
    [/^U[12]/, 'mk'], [/^U[567]/, 'sk'], [/^U[890]/, 'ba'],
    [/^V[ABCDE]/, 'at'], [/^V[FGHJKLMNPR]/, 'fr'], [/^V[STUVW]/, 'es'], [/^V[XYZ12]/, 'fr'],
    [/^V[345]/, 'hr'], [/^V[678]/, 'ee'],
    [/^W/, 'de'],
    [/^X[ABC]/, 'bg'], [/^X[DE]/, 'ru'], [/^X[FGH]/, 'gr'], [/^X[JK]/, 'ru'],
    [/^X[LMNPR]/, 'nl'], [/^X[STUVW]/, 'ru'], [/^X[XY]/, 'lu'], [/^X[Z1234567890]/, 'ru'],
    [/^Y[ABCDE]/, 'be'], [/^Y[FGHJK]/, 'fi'], [/^Y[N]/, 'mt'], [/^Y[STUVW]/, 'se'],
    [/^Y[XYZ12]/, 'no'], [/^Y[345]/, 'by'], [/^Y[678]/, 'ua'],
    [/^Z[ABCDEFGHJKLMNPRSTU]/, 'it'], [/^Z[XYZ]/, 'si'], [/^Z[1]/, 'sm'],
    [/^Z[345]/, 'lt'], [/^Z[67890]/, 'ru'],
    [/^1/, 'us'],
    [/^2[ABCDEFGHJKLMNPRSTUVWXYZ12345]/, 'ca'],
    [/^3[ABCDEFGHJKLMNPRSTUVWX]/, 'mx'], [/^3[4]/, 'ni'], [/^3[5]/, 'do'],
    [/^3[6]/, 'hn'], [/^3[7]/, 'pa'], [/^3[89]/, 'pr'],
    [/^4/, 'us'], [/^5/, 'us'],
    [/^6[ABCDEFGHJKLMNPRSTUVWX]/, 'au'], [/^6[YZ1]/, 'nz'],
    [/^7/, 'us'],
    [/^8[ABCDE]/, 'ar'], [/^8[FG]/, 'cl'], [/^8[LMN]/, 'ec'], [/^8[ST]/, 'pe'],
    [/^8[XYZ]/, 've'], [/^8[2]/, 'bo'], [/^8[4]/, 'cr'],
    [/^9[ABCDE]/, 'br'], [/^9[FG]/, 'co'], [/^9[STUV]/, 'uy'], [/^9[1234567890]/, 'br'],
];

/**
 * Изготовитель по трёхзначному WMI — ЯДРО, известное без загрузки данных.
 *
 * Полная таблица (Wikibooks «World Manufacturer Identifier», CC BY-SA 4.0,
 * плюс наши дополнения и замеры) лежит в public/data/vin/wmi.json и
 * подключается через useWmiData(); /vin-decoder ждёт её перед разбором.
 * Ядро нужно тем, кто разбирает VIN мимоходом и данные не грузит
 * (/car-paint-code узнаёт марку по номеру с таблички), и как запасной путь,
 * если данные не пришли. Сборщик данных (scripts/build-vin-wmi.mjs) кладёт
 * ядро в полную таблицу как есть, и тест держит их равными: одна строка
 * не может называть WMI по-разному в двух местах.
 *
 * Марка хранится латиницей, уточнение — через `|` ТОКЕНОМ: двухбуквенный
 * код страны или `#ключ` (переводится в index.js), иначе русская строка
 * печаталась бы во всех десяти локалях.
 *
 * ⚠️ Выдумывать марку по похожему префиксу нельзя: ошибка в марке хуже
 * отсутствия марки.
 */
export const CORE_WMI = {
    // ── Япония ────────────────────────────────────────────────────────────
    JHM: 'Honda', JHL: 'Honda', JHG: 'Honda', JH4: 'Acura', JH2: 'Honda|#moto',
    JT2: 'Toyota', JTD: 'Toyota', JTE: 'Toyota', JTH: 'Lexus', JTJ: 'Lexus',
    JTK: 'Toyota', JTL: 'Toyota', JTM: 'Toyota', JTN: 'Toyota', JTG: 'Toyota',
    JN1: 'Nissan', JN6: 'Nissan', JN8: 'Nissan', JNK: 'Infiniti', JNR: 'Infiniti',
    JA3: 'Mitsubishi', JA4: 'Mitsubishi', JMB: 'Mitsubishi', JMY: 'Mitsubishi',
    JF1: 'Subaru', JF2: 'Subaru', JF3: 'Subaru',
    JM1: 'Mazda', JM3: 'Mazda', JMZ: 'Mazda',
    JS1: 'Suzuki', JS2: 'Suzuki', JS3: 'Suzuki', JSA: 'Suzuki',
    JYA: 'Yamaha', JKA: 'Kawasaki', JD1: 'Daihatsu', JL5: 'Hino',
    // ── Корея ─────────────────────────────────────────────────────────────
    KMH: 'Hyundai', KMF: 'Hyundai', KMJ: 'Hyundai', KM8: 'Hyundai',
    KNA: 'Kia', KND: 'Kia', KNE: 'Kia', KNC: 'Kia', KNM: 'Renault Korea',
    KL1: 'GM Korea (Chevrolet)', KL3: 'GM Korea (Chevrolet)', KL5: 'GM Korea',
    KLA: 'Daewoo', KPT: 'SsangYong', KPA: 'SsangYong',
    // ── Китай ─────────────────────────────────────────────────────────────
    LFV: 'FAW-Volkswagen', LSV: 'SAIC Volkswagen', LVS: 'Changan Ford',
    LBV: 'BMW Brilliance', LGB: 'Dongfeng Nissan', LGW: 'Great Wall / Haval',
    LZW: 'SAIC-GM-Wuling', LSJ: 'MG / SAIC', LRW: 'Tesla|#shanghai',
    LYV: 'Volvo|cn', LJD: 'Yueda Kia', LNB: 'BAIC', L6T: 'Geely / Zeekr / Lynk & Co',
    LVV: 'Chery', LVH: 'Dongfeng Honda', LDC: 'Dongfeng Peugeot Citroën',
    LFP: 'FAW', LHG: 'GAC Honda', LMG: 'GAC', LJ1: 'JAC',
    // ── США ───────────────────────────────────────────────────────────────
    '1G1': 'Chevrolet', '1G6': 'Cadillac', '1GC': 'Chevrolet|#truck',
    '1GT': 'GMC', '1GK': 'GMC', '1GY': 'Cadillac', '1G4': 'Buick',
    '1FA': 'Ford', '1FT': 'Ford|#truck', '1FM': 'Ford|#suv',
    '1FD': 'Ford|#truck', '1FB': 'Ford',
    '1C3': 'Chrysler / Dodge', '1C4': 'Jeep / Chrysler', '1C6': 'Ram',
    '1J4': 'Jeep', '1J8': 'Jeep', '1B3': 'Dodge', '1D3': 'Dodge',
    '1HG': 'Honda|us', '19X': 'Honda|us', '1N4': 'Nissan|us',
    '1N6': 'Nissan|us', '1YV': 'Mazda|us', '1VW': 'Volkswagen|us',
    '1ME': 'Mercury', '1LN': 'Lincoln', '1L1': 'Lincoln',
    '4T1': 'Toyota|us', '4T3': 'Toyota|us', '4S3': 'Subaru|us',
    '4S4': 'Subaru|us', '4US': 'BMW|us', '4JG': 'Mercedes-Benz|us',
    '5YJ': 'Tesla', '5N1': 'Nissan|us', '5NP': 'Hyundai|us',
    '5TD': 'Toyota|us', '5TF': 'Toyota|us', '5XY': 'Kia|us',
    '5FN': 'Honda|us', '5J6': 'Honda|us', '5UX': 'BMW|us',
    '5LM': 'Lincoln', '5GA': 'Buick',
    // ── Канада и Мексика ──────────────────────────────────────────────────
    '2T1': 'Toyota|ca', '2T3': 'Toyota|ca', '2HG': 'Honda|ca',
    '2HK': 'Honda|ca', '2HJ': 'Honda|ca', '2G1': 'Chevrolet|ca',
    '2C3': 'Chrysler / Dodge|ca', '2C4': 'Chrysler / Dodge|ca', '2FA': 'Ford|ca',
    '2LM': 'Lincoln|ca',
    '3N1': 'Nissan|mx', '3VW': 'Volkswagen|mx', '3FA': 'Ford|mx',
    '3GN': 'Chevrolet|mx', '3C4': 'Jeep / Dodge / Chrysler / Fiat|mx', '3KP': 'Kia|mx',
    '3MZ': 'Mazda|mx', '3MY': 'Toyota (Mazda)|mx',
    // ── Германия ──────────────────────────────────────────────────────────
    WVW: 'Volkswagen', WV1: 'Volkswagen|#commercial', WV2: 'Volkswagen|#commercial',
    WAU: 'Audi', WA1: 'Audi', WUA: 'Audi Sport',
    WBA: 'BMW', WBS: 'BMW M', WBY: 'BMW i', WBX: 'BMW',
    WDB: 'Mercedes-Benz', WDC: 'Mercedes-Benz', WDD: 'Mercedes-Benz',
    WDF: 'Mercedes-Benz|#commercial', W1K: 'Mercedes-Benz', W1N: 'Mercedes-Benz',
    W1V: 'Mercedes-Benz|#commercial', WMX: 'Mercedes-AMG',
    WP0: 'Porsche', WP1: 'Porsche|#suv',
    WF0: 'Ford|#eu', W0L: 'Opel', W0V: 'Opel', WMW: 'MINI',
    WME: 'smart', WMA: 'MAN', WEB: 'EvoBus', WJM: 'Iveco|de',
    // ── Франция ───────────────────────────────────────────────────────────
    // VNV — Nissan, собранный Renault во Франции; VXK — Opel/Vauxhall
    // (до 22.10.2026 стояли «Renault (коммерческие)» и «Nissan (Франция)»;
    // реестр: VNV — 115 из 115 Nissan, VXK — 484 из 484 Opel/Vauxhall).
    VF1: 'Renault', VF3: 'Peugeot', VF7: 'Citroën', VF6: 'Renault Trucks',
    VF9: 'Bugatti', VNK: 'Toyota|fr', VNV: 'Nissan|fr',
    VR1: 'DS Automobiles', VR3: 'Peugeot', VR7: 'Citroën', VXK: 'Opel / Vauxhall',
    // ── Италия ────────────────────────────────────────────────────────────
    ZFA: 'Fiat', ZFC: 'Fiat|#commercial', ZFF: 'Ferrari', ZAM: 'Maserati',
    ZAR: 'Alfa Romeo', ZLA: 'Lancia', ZDM: 'Ducati', ZD4: 'Aprilia',
    ZCG: 'Cagiva', ZAP: 'Piaggio', ZCF: 'Iveco',
    // ── Испания ───────────────────────────────────────────────────────────
    VSS: 'SEAT', VSK: 'Nissan|es', VSE: 'Santana', VSX: 'Opel|es',
    // ── Швеция ────────────────────────────────────────────────────────────
    YV1: 'Volvo', YV4: 'Volvo', YV2: 'Volvo Trucks', YS3: 'Saab', YS2: 'Scania',
    // ── Великобритания ────────────────────────────────────────────────────
    SAJ: 'Jaguar', SAL: 'Land Rover', SAR: 'Rover', SCC: 'Lotus',
    SCA: 'Rolls-Royce', SCB: 'Bentley', SCF: 'Aston Martin',
    SHH: 'Honda|gb', SHS: 'Honda|gb',
    SJN: 'Nissan|gb', SB1: 'Toyota|gb',
    SDB: 'Peugeot|gb', SFD: 'LDV', SAX: 'MG Rover',
    // ── Чехия, Словакия, Венгрия, Польша, Румыния ─────────────────────────
    TMB: 'Škoda', TMA: 'Hyundai|cz', TMP: 'Škoda|#commercial',
    TMK: 'Karosa', TRU: 'Audi|hu', TMT: 'Tatra',
    UU1: 'Dacia|ro', UU2: 'Dacia', UU6: 'Daewoo|ro',
    SUP: 'FSO / Daewoo-FSO', SU9: 'Solaris', SUL: 'FSC',
    // ── Турция ────────────────────────────────────────────────────────────
    NM0: 'Ford Otosan', NM4: 'Tofaş / Fiat', NMT: 'Toyota|tr',
    NLH: 'Hyundai|tr', NLE: 'Mercedes-Benz Türk', NLA: 'Honda|tr',
    // ── Россия и соседи ───────────────────────────────────────────────────
    XTA: 'AvtoVAZ (Lada)', XTT: 'UAZ', XTC: 'KAMAZ', XTH: 'GAZ',
    XTY: 'GAZ', XTF: 'GAZ|#commercial', XW8: 'Volkswagen Group Rus',
    X7L: 'Renault / Moskvich (2022–)', X4X: 'BMW|#avtotor', X9L: 'GM-AvtoVAZ',
    X96: 'GAZ|#commercial', X89: '#special|ru',
    Z94: 'Hyundai / Kia (HMMR) · Solaris (AGR, 2023–)', Z8N: 'Nissan|ru', Z6F: 'Ford Sollers',
    // Z8T is PSMA Rus, Kaluga — Mitsubishi Outlander / Pajero Sport, Peugeot,
    // Citroën (UAZ is XTT). Was 'UAZ' until 23.09.2026.
    Z7G: 'GAZ', Z8T: 'PSMA Rus (Peugeot / Citroën / Mitsubishi)|ru',
    // XUU — Avtotor (Калининград), а не ZAZ, как стояло до 22.10.2026.
    XUU: 'Chevrolet / Kaiyi|#avtotor', XWE: 'Hyundai / Kia|#avtotor', XWD: 'BAIC|#avtotor',
    XZG: 'Haval (Haval Motor Rus)', EDE: 'Tenet (AGR)', EAN: 'Evolute', EBE: 'Sollers',
    Y6D: 'ZAZ|ua', XWB: 'UzAuto|#uz',
    XW7: 'Toyota|ru', X7M: 'Hyundai|ru',
    // ── Индия, Бразилия, ЮАР и прочие ─────────────────────────────────────
    MAT: 'Tata', MA1: 'Mahindra', MA3: 'Maruti Suzuki', MA6: 'GM|in',
    MAL: 'Hyundai|in', MBH: 'Suzuki|in', MEE: 'Renault|in',
    '9BW': 'Volkswagen|br', '9BD': 'Fiat|br',
    '93Y': 'Renault|br', '93H': 'Honda|br',
    '9BF': 'Ford|br', '9BG': 'Chevrolet|br',
    '8AP': 'Fiat|ar', '8AF': 'Ford|ar', '8AG': 'Chevrolet|ar',
    AAV: 'Volkswagen|za', ADM: 'General Motors|za', AHT: 'Toyota|za',
    '6G1': 'Holden', '6F4': 'Ford|au', '6MM': 'Mitsubishi|au',
    // ── Новые китайские марки (ряд H по ISO 2021) и электромобили ────────
    HXM: 'Xiaomi', HLX: 'Li Auto', HVW: 'Volkswagen Anhui', HJR: 'Jetour (Chery)',
    HJN: 'NIO', LGX: 'BYD', LC0: 'BYD', '7SA': 'Tesla', WVG: 'Volkswagen|#suv',
};

/** Полная таблица WMI, если её подали (см. useWmiData). */
let DATA = null;

/**
 * Подать полную таблицу WMI (содержимое public/data/vin/wmi.json).
 * Строка таблицы: [марка, запись Wikibooks, машин в замере, % контрольной
 * цифры, % года, машин с кодом года на 10-м месте, самый ранний год
 * регистрации (0,5-й перцентиль)] — замеры null, если машин
 * меньше порога. Доля года считается только по машинам, у которых на 10-м
 * месте вообще стоит код года: европейский BMW несёт там «0» (года нет — это
 * видно по самому знаку), а коды года, которые он несёт, сходятся в 99 %.
 * У Renault они сходятся в 13 % — там буква вводит в заблуждение.
 */
export function useWmiData(data) {
    DATA = data && data.wmi && typeof data.wmi === 'object' ? data : null;
    return !!DATA;
}

export function hasWmiData() { return !!DATA; }

/** Символы, которых в VIN не бывает, и во что их обычно ошибочно превращают. */
export const CONFUSABLE = { I: '1', O: '0', Q: '0' };

/** Нормализовать введённое: убрать пробелы и дефисы, поднять регистр. */
export function normalize(raw) {
    return String(raw || '').toUpperCase().replace(/[\s\-–—_.]/g, '');
}

/**
 * Номер кузова японской машины внутреннего рынка (車台番号): код модели,
 * дефис, порядковый номер — `BNR32-305366`, `ZVW30-1234567`. Это НЕ VIN:
 * у него нет ни кода страны, ни контрольной цифры, ни кода года, и
 * проверять его правилами VIN значит отказать исправному номеру.
 *
 * Узнаётся только С ДЕФИСОМ: без него «BNR32305366» неотличим от
 * недонабранного VIN. Код модели — 2–8 знаков и хотя бы одна буква (иначе
 * под шаблон попадает телефонный номер), номер — 4–7 цифр. Тире, длинное
 * тире и японская черта «ー» читаются как дефис.
 */
const FRAME_RE = /^(?=[A-Z0-9]*[A-Z])([A-Z0-9]{2,8})-(\d{4,7})$/;
export function frameNumber(raw) {
    const s = String(raw || '').toUpperCase().replace(/\s+/g, '').replace(/[‐‑‒–—―−－ー]/g, '-');
    const m = s.match(FRAME_RE);
    return m ? { model: m[1], serial: m[2], text: m[1] + '-' + m[2] } : null;
}

/** Контрольная цифра, вычисленная по всем остальным знакам. */
export function computeCheckDigit(vin) {
    let sum = 0;
    for (let i = 0; i < 17; i++) {
        const c = vin[i];
        const v = /[0-9]/.test(c) ? +c : TRANSLIT[c];
        if (v == null) return null;           // недопустимый символ — считать нечего
        sum += v * WEIGHTS[i];
    }
    const r = sum % 11;
    return r === 10 ? 'X' : String(r);
}

/** Страна по первым двум знакам (таблица ISO 3780, ред. 2021). */
export function countryOf(vin) {
    for (const [re, code] of COUNTRY_RANGES) if (re.test(vin)) return code;
    return null;
}

/**
 * Изготовитель и замер по WMI.
 *
 * Мелкий изготовитель (меньше 1000 машин в год) получает WMI с девяткой на
 * третьем месте, а себя называет знаками 12–14: `1G9/492`. Такой ключ
 * проверяется первым — иначе любой мелкий завод с тем же «1G9» получил бы
 * чужое имя.
 */
function makerOf(vin) {
    const wmi = vin.slice(0, 3);
    const small = wmi[2] === '9' ? wmi + '/' + vin.slice(11, 14) : null;
    if (DATA) {
        const key = (small && DATA.wmi[small]) ? small : (DATA.wmi[wmi] ? wmi : null);
        if (key) {
            const row = DATA.wmi[key];
            return {
                key, m: row[0] || null, d: row[1] || null,
                n: row[2] || 0, cd: row[3] == null ? null : +row[3],
                yr: row[4] == null ? null : +row[4], yrN: row[5] || 0, y0: row[6] || 0,
            };
        }
        /* Измерение бывает и без имени: WMI нет ни в одном списке, а машины
           с ним в реестре есть. Тогда имени нет, а замер остаётся. */
        const st = DATA.stats && DATA.stats[wmi];
        if (st) return { key: wmi, m: CORE_WMI[wmi] || null, d: null, n: st[0] || 0, cd: st[1], yr: st[2], yrN: st[3] || 0, y0: st[4] || 0 };
    }
    const m = CORE_WMI[wmi];
    return m ? { key: wmi, m, d: null, n: 0, cd: null, yr: null, yrN: 0, y0: 0 } : null;
}

/**
 * Вердикт по контрольной цифре.
 *
 * `use` — как этот изготовитель заполняет девятый знак:
 *   'used'     — измерено ≥ 95 % совпадений, либо замера нет, а регион его
 *                требует (Северная Америка, Китай);
 *   'mixed'    — 30–95 %: цифра есть лишь у части машин (экспортные
 *                исполнения), расхождение само по себе ничего не значит;
 *   'not_used' — меньше 30 % (случайный знак сходится в 9 %), либо замера
 *                нет и регион цифру не требует.
 * `state` — что показать: 'ok' · 'bad' (требуется — и не сошлось) ·
 *   'bad_soft' (изготовитель обычно ставит, но не обязан) · 'mixed' ·
 *   'not_used' · 'uncomputable'.
 */
function checksumOf(vin, mk) {
    const expected = computeCheckDigit(vin);
    const actual = vin[8];
    if (expected == null) return { state: 'uncomputable' };
    const match = expected === actual;
    const mandatory = MANDATORY.test(vin);
    const measured = mk && mk.cd != null && mk.n ? { pct: mk.cd, n: mk.n } : null;
    let use;
    if (measured) use = measured.pct >= CD_USED ? 'used' : measured.pct >= CD_MIXED ? 'mixed' : 'not_used';
    else use = mandatory ? 'used' : 'not_used';
    let state;
    if (use === 'used') state = match ? 'ok' : (mandatory ? 'bad' : 'bad_soft');
    else if (use === 'mixed') state = match ? 'ok' : 'mixed';
    else state = 'not_used';
    return { state, use, expected, actual, match, mandatory, measured };
}

/**
 * Год модели по 10-му знаку.
 *
 * `policy`:
 *   'reliable'   — замер ≥ 95 % совпадений с годом регистрации (среди машин,
 *                  у которых там код года), либо замера нет, а регион год
 *                  там требует;
 *   'unreliable' — замер ниже: изготовитель кладёт туда другое у заметной
 *                  части машин, называть год нельзя;
 *   'unmeasured' — замера нет и правило региона не действует.
 * `isCode` — стоит ли там вообще знак кода года (0, U, Z — нет).
 *
 * Неоднозначность цикла здесь НЕ баг стандарта, а его свойство: «G» это и
 * 1986, и 2016. Для лёгких машин Северной Америки её снимает 7-й знак (цифра →
 * до 2010, буква → с 2010); для остальных снять её из самого номера НЕЛЬЗЯ.
 */
function yearOf(vin, mk) {
    const measured = mk && mk.yr != null && mk.yrN ? { pct: mk.yr, n: mk.yrN } : null;
    let policy;
    if (measured) policy = measured.pct >= YEAR_RELIABLE ? 'reliable' : 'unreliable';
    else policy = MANDATORY.test(vin) ? 'reliable' : 'unmeasured';

    const idx = YEAR_CODES.indexOf(vin[9]);
    if (idx < 0) return { policy, measured, isCode: false, code: vin[9], years: [], both: [], certain: false };
    const early = 1980 + idx;         // 1980…2009
    const late = early + 30;          // 2010…2039
    if (NA_LIGHT.test(vin)) {
        const resolved = /[0-9]/.test(vin[6]) ? early : late;
        return { policy, measured, isCode: true, code: vin[9], years: [resolved], certain: true, both: [early, late] };
    }
    const now = new Date().getFullYear();
    // Отсекаем будущее: модельный год на два года вперёд бывает, дальше — нет.
    let opts = [early, late].filter((y) => y <= now + 2);
    /* И прошлое, которого у этого кода не было: «S» — это 1995 или 2025, а
       ни одной машины Xiaomi HXM в реестре старше 2024 года нет. Порог —
       самый ранний год регистрации машин с этим WMI (0,5-й перцентиль, замер),
       с запасом в два года; без замера ничего не отсекается. */
    let cut = null;
    const y0 = mk && mk.y0 ? mk.y0 : 0;
    if (opts.length === 2 && y0 && early < y0 - 2) { cut = early; opts = [late]; }
    return {
        policy, measured, isCode: true, code: vin[9],
        years: opts.length ? opts : [early], certain: opts.length === 1, both: [early, late], cut, y0,
    };
}

/**
 * Название изготовителя на языке страницы.
 *
 * ⚠️⚠️ Таблица WMI хранит МАРКУ латиницей и, через `|`, ТОКЕН уточнения —
 * `'Honda|us'`, `'Ford|#truck'`. До 10.09.2026 уточнение лежало в таблице
 * готовой строкой по-русски («Honda (США)»), и эта строка печаталась как есть
 * во всех десяти локалях. Токен переводит страница: двухбуквенный — названием
 * страны (`country.<код>`), `#…` — своим ключом (`mk_<имя>`). `tr(key)`
 * возвращает перевод. Общая для /vin-decoder и /car-check.
 */
export function formatMaker(raw, tr) {
    if (!raw) return '';
    const part = (tok) => (tok.charAt(0) === '#' ? tr('mk_' + tok.slice(1)) : tr('country.' + tok));
    const bits = String(raw).split('|');
    const base = bits[0].charAt(0) === '#' ? part(bits[0]) : bits[0];
    const qual = bits.slice(1).map(part).filter(Boolean).join(', ');
    return qual ? base + ' (' + qual + ')' : base;
}

/** Страна, которую называет сама запись изготовителя (`Toyota|nz` → 'nz'). */
export function makerCountry(raw) {
    if (!raw) return null;
    const tok = String(raw).split('|').slice(1).find((x) => /^[a-z]{2}$/.test(x));
    return tok || null;
}

/**
 * Полный разбор.
 *
 * @returns {object} всегда объект; `valid` говорит, годится ли строка как VIN,
 *          `issues` — что именно не так, `kind: 'frame'` — это номер кузова
 *          японской машины, а не VIN.
 */
export function decode(raw) {
    const frame = frameNumber(raw);
    if (frame) return { vin: frame.text, valid: false, kind: 'frame', frame, issues: [] };

    const vin = normalize(raw);
    const issues = [];

    if (!vin) return { vin: '', valid: false, issues: ['empty'] };

    // Подсказка по путанице символов — ДО отказа: человек почти всегда
    // набрал O вместо нуля, и сказать об этом полезнее, чем «неверный VIN».
    const confused = [];
    for (const ch of vin) if (CONFUSABLE[ch] && !confused.includes(ch)) confused.push(ch);

    const suggestion = confused.length
        ? vin.split('').map((c) => CONFUSABLE[c] || c).join('')
        : null;

    if (vin.length !== 17) issues.push(vin.length < 17 ? 'too_short' : 'too_long');
    if (confused.length) issues.push('confusable');
    if (/[^A-HJ-NPR-Z0-9]/.test(vin)) issues.push('bad_chars');

    if (issues.length) {
        return {
            vin, valid: false, issues, length: vin.length,
            confusable: confused, suggestion,
        };
    }

    const country = countryOf(vin);
    const wmi = vin.slice(0, 3);
    const mk = makerOf(vin);

    return {
        vin, valid: true, kind: 'vin', issues: [],
        wmi,
        wmiKey: mk ? mk.key : wmi,
        manufacturer: mk ? mk.m : null,
        makerDetail: mk ? mk.d : null,
        country,
        vds: vin.slice(3, 8),
        checkDigit: vin[8],
        checksum: checksumOf(vin, mk),
        yearCode: vin[9],
        year: yearOf(vin, mk),
        plantCode: vin[10],
        serial: vin.slice(11),
        /* Серийный номер меньше шести цифр у мелкосерийных изготовителей
           означает другую разбивку — говорить о «номере по порядку» там нельзя. */
        smallSeries: /^[0-9]{0,3}$/.test(vin.slice(11).replace(/[A-Z]/g, '')),
    };
}
